/**
 * Inspector's "Before applying" checks, typed: a fill-in names the value the
 * fix needs (and the `{{key}}` token it fills), a confirm is an attestation,
 * an approval names who must approve the amended document, a follow-up is
 * work for afterwards, a caveat is a warning to read. A plain string from an
 * older proposal reads as a confirm.
 *
 * The checklist never blocks a decision. Ticks, filled values and recorded
 * approvals are not stored yet, so the controls show what each check asks
 * for and say plainly that recording comes later.
 *
 * @module components/regulation/finding/changes/FixChecks
 */
import { typedFixCheck, type OutstandingCheck, type TypedFixCheck } from "@repo/shared/findingChanges";
import { CircleAlertIcon, ListTodoIcon, PenLineIcon, ShieldCheckIcon, StampIcon, TriangleAlertIcon } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "~/lib/utils";

const KIND: Readonly<Record<TypedFixCheck["kind"], { readonly label: string; readonly icon: ReactNode }>> = {
  fill: { label: "Fill in", icon: <PenLineIcon aria-hidden className="size-3.5" /> },
  confirm: { label: "Confirm", icon: <ShieldCheckIcon aria-hidden className="size-3.5" /> },
  approval: { label: "Approval", icon: <StampIcon aria-hidden className="size-3.5" /> },
  followup: { label: "Follow-up", icon: <ListTodoIcon aria-hidden className="size-3.5" /> },
  caveat: { label: "Caveat", icon: <TriangleAlertIcon aria-hidden className="size-3.5" /> },
};

/** Counts per kind, for the summary line. */
export function checkSummary(checks: ReadonlyArray<OutstandingCheck>): string {
  const typed = checks.map(typedFixCheck);
  const count = (kind: TypedFixCheck["kind"]) => typed.filter((check) => check.kind === kind).length;
  return (
    [
      [count("fill"), "to fill in"],
      [count("confirm"), "to confirm"],
      [count("approval"), "approval"],
      [count("followup"), "follow-up"],
      [count("caveat"), "caveat"],
    ] as const
  )
    .filter(([amount]) => amount > 0)
    .map(([amount, label]) => `${amount} ${label}${amount > 1 && (label === "follow-up" || label === "caveat" || label === "approval") ? "s" : ""}`)
    .join(" · ");
}

export function FixChecks({ checks }: { readonly checks: ReadonlyArray<OutstandingCheck> }) {
  const typed = checks.map(typedFixCheck);
  const caveats = typed.filter((check) => check.kind === "caveat");
  const rows = typed.filter((check) => check.kind !== "caveat");
  return (
    <div className="mt-3 space-y-3" data-fix-checks="">
      <p className="text-xs text-muted-foreground">{checkSummary(checks)}</p>
      {caveats.map((check, index) => (
        <p key={`caveat:${index}`} className="flex items-start gap-2 rounded-md bg-amber-400/10 px-3 py-2 text-[13px] leading-5 text-amber-900 dark:text-amber-200">
          <TriangleAlertIcon aria-hidden className="mt-0.5 size-3.5 shrink-0" />
          {check.text}
        </p>
      ))}
      <ul className="divide-y divide-border/60 overflow-hidden rounded-lg border border-border/70">
        {rows.map((check, index) => (
          <li key={`${check.kind}:${index}`} className="flex items-start gap-3 px-3 py-2.5 text-[13px] leading-5" data-check-kind={check.kind}>
            <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center text-muted-foreground">{KIND[check.kind].icon}</span>
            <span className="min-w-0 flex-1">
              <span className="block text-foreground/90">{check.text}</span>
              {check.kind === "fill" && check.field ? (
                <span className="mt-1.5 flex flex-wrap items-center gap-2">
                  <span className="text-xs text-muted-foreground">{check.field.label}</span>
                  <input
                    disabled
                    aria-label={check.field.label}
                    placeholder={check.field.placeholder ?? `{{${check.field.key}}}`}
                    className="h-7 w-56 max-w-full rounded-md border border-input bg-background px-2 text-xs text-muted-foreground"
                  />
                  <code className="text-[11px] text-muted-foreground">{`{{${check.field.key}}}`}</code>
                </span>
              ) : null}
              {check.kind === "approval" && check.approver ? (
                <span className="mt-0.5 block text-xs text-muted-foreground">Approver: {check.approver}</span>
              ) : null}
            </span>
            <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium", check.blocking ? "bg-destructive/10 text-destructive" : "bg-muted text-muted-foreground")}>
              {check.blocking ? "Required" : KIND[check.kind].label}
            </span>
          </li>
        ))}
      </ul>
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <CircleAlertIcon aria-hidden className="size-3.5" />
        The checklist never blocks a decision. Tick checks and fill in values in the finding's checklist.
      </p>
    </div>
  );
}
